import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Database, HelpCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PERIOD_PATTERN_PRESETS, getTotalWeeks, validatePeriodRatios } from '@/types/settings';
import type { PeriodPatternPreset } from '@/types/settings';
import { DepreciationCalculator } from '@/lib/depreciation-calculator';
import type { PeriodsTabProps } from './types';

export function PeriodsTab({
  options,
  onOptionsChange,
  setHasUnsavedChanges,
}: Readonly<PeriodsTabProps>): React.ReactElement {
  return (
    <div className="space-y-6">
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            Period Pattern Configuration
          </CardTitle>
          <CardDescription>
            Configure the number of weeks in each period for Period Ratio depreciation calculations.
            This determines how annual depreciation is distributed across accounting periods.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pattern Preset Selection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="periodPattern" className="text-base font-medium">Period Pattern</Label>
              <Select
                value={options.periods.preset}
                onValueChange={(value: PeriodPatternPreset) => {
                  if (value === 'custom') {
                    onOptionsChange(prev => ({
                      ...prev,
                      periods: { ...prev.periods, preset: 'custom' }
                    }));
                  } else {
                    const presetRatios = PERIOD_PATTERN_PRESETS[value].ratios;
                    onOptionsChange(prev => ({
                      ...prev,
                      periods: { preset: value, periodRatios: [...presetRatios] }
                    }));
                    DepreciationCalculator.setPeriodRatios(presetRatios);
                  }
                  setHasUnsavedChanges(true);
                }}
              >
                <SelectTrigger id="periodPattern" className="w-full md:w-[300px]">
                  <SelectValue placeholder="Select a pattern" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PERIOD_PATTERN_PRESETS).map(([key, preset]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex flex-col">
                        <span>{preset.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">
                    <span>Custom Pattern</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {options.periods.preset !== 'custom' && PERIOD_PATTERN_PRESETS[options.periods.preset as Exclude<PeriodPatternPreset, 'custom'>] && (
                <p className="text-sm text-muted-foreground">
                  {PERIOD_PATTERN_PRESETS[options.periods.preset as Exclude<PeriodPatternPreset, 'custom'>].description}
                </p>
              )}
            </div>

            {/* Pattern Summary */}
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Current Pattern</Label>
                <Badge variant={(() => {
                  const validation = validatePeriodRatios(options.periods.periodRatios);
                  return validation.valid ? 'default' : 'destructive';
                })()}>
                  {getTotalWeeks(options.periods.periodRatios).toFixed(1)} weeks
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground font-mono">
                {options.periods.periodRatios.map(r => r.toString()).join(', ')}
              </p>
              {(() => {
                const validation = validatePeriodRatios(options.periods.periodRatios);
                if (!validation.valid) {
                  return (
                    <Alert variant="destructive" className="mt-3">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{validation.message}</AlertDescription>
                    </Alert>
                  );
                }
                return null;
              })()}
            </div>

            <Separator />

            {/* Custom Period Configuration - Only shown when custom is selected */}
            {options.periods.preset === 'custom' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">Custom Period Weeks</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Enter the number of weeks for each accounting period. The total should equal approximately 52 weeks.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((period) => (
                    <div key={period} className="space-y-2">
                      <Label htmlFor={`period${period}`} className="text-sm">Period {period}</Label>
                      <Input
                        id={`period${period}`}
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={options.periods.periodRatios[period - 1]}
                        onChange={(e) => {
                          const newRatios = [...options.periods.periodRatios];
                          newRatios[period - 1] = parseFloat(e.target.value) ?? 0;
                          onOptionsChange(prev => ({
                            ...prev,
                            periods: { ...prev.periods, periodRatios: newRatios }
                          }));
                          DepreciationCalculator.setPeriodRatios(newRatios);
                          setHasUnsavedChanges(true);
                        }}
                        className="h-9"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total Weeks Display */}
            <div className="flex items-center justify-between rounded-lg border bg-primary/5 p-4">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Total Weeks</Label>
                <p className="text-sm text-muted-foreground">
                  Sum of all period weeks (should be approximately 52)
                </p>
              </div>
              <div className={cn(
                "text-2xl font-bold",
                (() => {
                  const total = getTotalWeeks(options.periods.periodRatios);
                  return total >= 51 && total <= 53 ? "text-primary" : "text-destructive";
                })()
              )}>
                {getTotalWeeks(options.periods.periodRatios).toFixed(1)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
